import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageSquare, Shield, CheckCircle, Send } from 'lucide-react';

const MEMBER = {
  name: 'Jamie Wallace',
  checkingLast4: '1842',
  checkingBalance: '$3,426.18',
  savingsLast4: '7710',
  savingsBalance: '$12,904.55',
  phoneLast4: '4421',
  transactions: [
    { merchant: 'Fuel Station',      amount: '$48.22',    when: 'today at 8:14 AM' },
    { merchant: 'Grocery Market',    amount: '$126.83',   when: 'yesterday at 6:42 PM' },
    { merchant: 'Payroll Deposit',   amount: '+$2,184.00',when: 'yesterday at 5:01 AM' },
    { merchant: 'Coffee Shop',       amount: '$6.91',     when: 'yesterday at 7:53 AM' },
    { merchant: 'Streaming Service', amount: '$18.99',    when: 'two days ago at 9:10 PM' },
  ],
};

// auth:  greeting → awaiting_phone → awaiting_otp → verified
// chat:  initial → asked_balances → asked_tx → ready_callback

const ChatInterface = ({ onRequestCallback, onTranscriptUpdate }) => {
  const [authPhase, setAuthPhase] = useState('greeting');
  const [chatPhase, setChatPhase] = useState('initial');
  const [messages, setMessages]   = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping]   = useState(false);
  const messagesEndRef = useRef(null);
  const transcriptRef  = useRef('');

  const appendTranscript = useCallback((speaker, text) => {
    transcriptRef.current += `\n${speaker}: ${text}`;
    onTranscriptUpdate?.(transcriptRef.current.trim());
  }, [onTranscriptUpdate]);

  const addMsg = (role, content) => {
    setMessages(prev => [...prev, { role, content, id: Date.now() + Math.random() }]);
    appendTranscript(role === 'bot' ? 'Agent' : 'Customer', content);
  };

  const botSay = useCallback((content, delay = 900) => new Promise(resolve => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, { role: 'bot', content, id: Date.now() + Math.random() }]);
      appendTranscript('Agent', content);
      resolve();
    }, delay);
  }), [appendTranscript]);

  // Initial greeting
  useEffect(() => {
    const t = setTimeout(() => {
      const greeting = `Welcome back, Miss Wallace. I see you're signed in to your CAP Credit Union account. Before we continue, for your security, please confirm the last four digits of your mobile number.`;
      setMessages([{ role: 'bot', content: greeting, id: 1 }]);
      appendTranscript('Agent', greeting);
      setAuthPhase('awaiting_phone');
    }, 700);
    return () => clearTimeout(t);
  }, [appendTranscript]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || isTyping) return;
    setInputValue('');
    addMsg('user', text);

    if (authPhase === 'awaiting_phone') {
      setAuthPhase('awaiting_otp');
      await botSay(`Thank you. I've matched that successfully. For one additional verification step, I've sent a one-time passcode to your mobile phone. Please enter it here.`);
    } else if (authPhase === 'awaiting_otp') {
      setAuthPhase('verified');
      await botSay(`Thank you. You're verified. How can I help you today?`);
    } else if (authPhase === 'verified') {
      await handleVerifiedInput(text);
    }
  };

  const handleVerifiedInput = async (text) => {
    const lower = text.toLowerCase();
    if (lower.includes('call me') || lower.includes('call back') || lower.includes('callback')) {
      await botSay(`Of course — I'll call you back right now on your mobile ending in ${MEMBER.phoneLast4}. Your authenticated session will continue seamlessly by voice.`, 600);
      setTimeout(() => onRequestCallback?.(), 900);
      return;
    }
    if (lower.includes('balance') || lower.includes('account') || lower.includes('check')) {
      setChatPhase('asked_balances');
      await botSay(
        `Absolutely. Your primary checking account ending in ${MEMBER.checkingLast4} has an available balance of ${MEMBER.checkingBalance}. Your savings account ending in ${MEMBER.savingsLast4} has a balance of ${MEMBER.savingsBalance}. Would you like recent transactions for checking, savings, or both?`
      );
    } else if (lower.includes('transaction') || lower.includes('recent') || lower.includes('checking')) {
      await showCheckingTransactions();
    } else {
      await botSay(`I can help you with your account balances, recent transactions, or other inquiries. What would you like to know?`);
    }
  };

  const showCheckingTransactions = async () => {
    setChatPhase('asked_tx');
    const list = MEMBER.transactions
      .map((t, i) => `${i + 1}. ${t.merchant} — ${t.amount}, ${t.when}`)
      .join('\n');
    await botSay(`Here are your five most recent checking transactions:\n\n${list}`);
  };

  const quickReplies = (() => {
    if (authPhase !== 'verified') return [];
    if (chatPhase === 'initial')        return ['Check my balances and recent transactions'];
    if (chatPhase === 'asked_balances') return ['Checking', 'Savings', 'Both'];
    return [];
  })();

  const handleQuickReply = async (text) => {
    addMsg('user', text);
    if (text === 'Checking') {
      await showCheckingTransactions();
    } else if (text === 'Savings') {
      await botSay(`Your savings account ending in ${MEMBER.savingsLast4} has a balance of ${MEMBER.savingsBalance} with no recent activity to report.`);
    } else if (text === 'Both') {
      setChatPhase('asked_tx');
      const list = MEMBER.transactions
        .map((t, i) => `${i + 1}. ${t.merchant} — ${t.amount}, ${t.when}`)
        .join('\n');
      await botSay(
        `Your savings account ending in ${MEMBER.savingsLast4} has a balance of ${MEMBER.savingsBalance}.\n\nYour five most recent checking transactions:\n\n${list}`
      );
    } else if (text === 'Check my balances and recent transactions') {
      setChatPhase('asked_balances');
      await botSay(
        `Absolutely. Your primary checking account ending in ${MEMBER.checkingLast4} has an available balance of ${MEMBER.checkingBalance}. Your savings account ending in ${MEMBER.savingsLast4} has a balance of ${MEMBER.savingsBalance}. Would you like recent transactions for checking, savings, or both?`
      );
    }
  };


  const inputPlaceholder =
    authPhase === 'awaiting_phone' ? 'Enter last 4 digits of mobile number...' :
    authPhase === 'awaiting_otp'   ? 'Enter one-time passcode...' :
    authPhase === 'verified'       ? 'Type a message...' : '';

  return (
    <div className="h-full flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-[#1a3a6b] px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">CAP Credit Union</p>
            <p className="text-white text-xs">Secure Web Chat</p>
          </div>
        </div>
        <div>
          {authPhase === 'verified' ? (
            <span className="flex items-center gap-1 text-xs bg-green-500/20 text-green-300 border border-green-400/30 px-2.5 py-1 rounded-full">
              <CheckCircle className="w-3 h-3" /> Verified
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs bg-white/10 text-white px-2.5 py-1 rounded-full">
              <Shield className="w-3 h-3" />
              {authPhase === 'greeting' ? 'Connecting...' : 'Verifying identity'}
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50 min-h-0">
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'bot' && (
              <div className="w-7 h-7 rounded-full bg-[#1a3a6b] flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                <MessageSquare className="w-3.5 h-3.5 text-white" />
              </div>
            )}
            <div
              className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-[#1a3a6b] text-white rounded-tr-sm'
                  : 'bg-white text-gray-800 border border-gray-200 rounded-tl-sm shadow-sm'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="w-7 h-7 rounded-full bg-[#1a3a6b] flex items-center justify-center mr-2 flex-shrink-0">
              <MessageSquare className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
              <div className="flex gap-1 items-center">
                {[0, 150, 300].map(delay => (
                  <span
                    key={delay}
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: `${delay}ms` }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick replies */}
      {quickReplies.length > 0 && (
        <div className="px-4 py-2 flex flex-wrap gap-2 border-t border-gray-100 bg-white flex-shrink-0">
          {quickReplies.map(r => (
            <button
              key={r}
              onClick={() => handleQuickReply(r)}
              disabled={isTyping}
              className="text-xs px-3 py-1.5 rounded-full border border-[#1a3a6b] text-[#1a3a6b] hover:bg-[#1a3a6b] hover:text-white transition-colors disabled:opacity-40"
            >
              {r}
            </button>
          ))}
        </div>
      )}

      {/* Input bar */}
      <div className="px-4 py-3 border-t border-gray-200 bg-white flex gap-2 flex-shrink-0">
        <input
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder={inputPlaceholder}
          disabled={authPhase === 'greeting' || isTyping}
          className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/30 disabled:bg-gray-50 disabled:text-gray-400"
        />
        <button
          onClick={handleSend}
          disabled={!inputValue.trim() || isTyping || authPhase === 'greeting'}
          className="bg-[#1a3a6b] text-white w-9 h-9 rounded-xl flex items-center justify-center hover:bg-[#0f2a5a] disabled:opacity-40 transition-colors flex-shrink-0"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ChatInterface;